import {
    HttpClient,
    HttpErrorResponse,
    HttpHeaders,
    HttpStatusCode,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { formatString } from '@app/helpers/text/format-string';
import { AppStore } from '@app/stores/app/app.store';
import { ICustomErrorResponse } from '@app/types/custom-error-response';
import { IValidationErrorResponse } from '@app/types/validation-error-response';
import { environment } from '@env/environment';
import { catchError, concatMap, from, Observable, of, retry, throwError } from 'rxjs';
import { MsalAuthService } from '../msal-auth/msal-auth.service';

@Injectable({
    providedIn: 'root',
})
export class RequestBuilderService {
    private apiUrl: string = environment.apiEndpoint;
    private http = inject(HttpClient);
    private appStore = inject(AppStore);
    translate = this.appStore.i18n;
    private msalAuthService = inject(MsalAuthService);
    private nextGenApiBase: string = environment.nextGenApiEndpoint;

    get<T>(
        endpointUrl: string,
        baseUrl: string | null = null,
        headers: HttpHeaders | null = null,
    ) {
        return this.validateToken(headers).pipe(
            concatMap((headers) => {
                return this.executeGet<T>(endpointUrl, baseUrl, headers);
            }),
        );
    }

    //note: this code is for public api use only/ no token required
    getAnonymous<T>(
        endpointUrl: string,
        baseUrl: string | null = null,
        headers: HttpHeaders | null = null,
    ) {
        return this.executeGet<T>(endpointUrl, baseUrl, headers);
    }

    downloadFile<T>(
        endpointUrl: string,
        param?: unknown,
        baseUrl: string | null = null,
        headers: HttpHeaders | null = null,
        contentType = 'application/pdf',
    ) {
        return this.validateToken(headers).pipe(
            concatMap((headers) => {
                headers?.append('Accept', contentType);
                return this.http
                    .post<T>(this.getApiUrl(baseUrl, endpointUrl), param, {
                        headers: headers,
                        responseType: 'blob' as 'json',
                    })
                    .pipe(catchError(this.handleError.bind(this)));
            }),
        );
    }

    getImg<T>(
        endpointUrl: string,
        baseUrl: string | null = null,
        headers: HttpHeaders | null = null,
        contentType = 'application/octet-stream',
    ) {
        return this.validateToken(headers).pipe(
            concatMap((headers) => {
                headers?.append('Accept', contentType);
                return this.http
                    .get<T>(this.getApiUrl(baseUrl, endpointUrl), {
                        headers: headers,
                        responseType: 'blob' as 'json',
                    })
                    .pipe(catchError(this.handleError.bind(this)));
            }),
        );
    }

    post<T>(
        endpointUrl: string,
        param: unknown,
        baseUrl: string | null = null,
        headers: HttpHeaders | null = null,
    ) {
        return this.validateToken(headers).pipe(
            concatMap((headers) => {
                return this.executePost<T>(endpointUrl, param, baseUrl, headers);
            }),
        );
    }
    //note: this code is for public api use only/ no token required
    postAnonymous<T>(
        endpointUrl: string,
        param: unknown,
        baseUrl: string | null = null,
        headers: HttpHeaders | null = null,
    ) {
        return this.executePost<T>(endpointUrl, param, baseUrl, headers);
    }

    put<T>(
        endpointUrl: string,
        param: unknown,
        baseUrl: string | null = null,
        headers: HttpHeaders | null = null,
    ) {
        return this.validateToken(headers).pipe(
            concatMap((headers) => {
                return this.executePut<T>(endpointUrl, param, baseUrl, headers);
            }),
        );
    }
    //note: this code is for public api use only/ no token required
    putAnonymous<T>(
        endpointUrl: string,
        param: unknown,
        baseUrl: string | null = null,
        headers: HttpHeaders | null = null,
    ) {
        return this.executePut<T>(endpointUrl, param, baseUrl, headers);
    }

    delete<T>(
        endpointUrl: string,
        baseUrl: string | null = null,
        headers: HttpHeaders | null = null,
    ) {
        return this.validateToken(headers).pipe(
            concatMap((headers) => {
                return this.executeDelete<T>(endpointUrl, baseUrl, headers);
            }),
        );
    }
    //note: this code is for public api use only/ no token required
    deleteAnonymous<T>(
        endpointUrl: string,
        baseUrl: string | null = null,
        headers: HttpHeaders | null = null,
    ) {
        return this.executeDelete<T>(endpointUrl, baseUrl, headers);
    }

    private executeGet<T>(
        endpointUrl: string,
        baseUrl: string | null = null,
        headers: HttpHeaders | null = null,
    ) {
        return this.http
            .get<T>(this.getApiUrl(baseUrl, endpointUrl), {
                headers: headers ?? undefined,
            })
            .pipe(
                retry({ count: 2, delay: 1000 }), //fault tolerance handling
                catchError(this.handleError.bind(this)),
            );
    }

    private executePost<T>(
        endpointUrl: string,
        param: unknown,
        baseUrl: string | null = null,
        headers: HttpHeaders | null = null,
    ) {
        return this.http
            .post<T>(this.getApiUrl(baseUrl, endpointUrl), param, {
                headers: headers ?? undefined,
            })
            .pipe(catchError(this.handleError.bind(this)));
    }

    private executePut<T>(
        endpointUrl: string,
        param: unknown,
        baseUrl: string | null = null,
        headers: HttpHeaders | null = null,
    ) {
        return this.http
            .put<T>(this.getApiUrl(baseUrl, endpointUrl), param, {
                headers: headers ?? undefined,
            })
            .pipe(catchError(this.handleError.bind(this)));
    }

    private executeDelete<T>(
        endpointUrl: string,
        baseUrl: string | null = null,
        headers: HttpHeaders | null = null,
    ) {
        return this.http
            .delete<T>(this.getApiUrl(baseUrl, endpointUrl), {
                headers: headers ?? undefined,
            })
            .pipe(catchError(this.handleError.bind(this)));
    }

    private handleError(httpError: HttpErrorResponse) {
        const customErrors = this.getCustomErrors(httpError);
        return throwError(() => {
            return customErrors;
        });
    }
    //note: this code is for replacing auth interceptor because own interceptors won't work with module federation
    // only the interceptors from the shell app will work
    private getHeaders(headers: HttpHeaders | null): HttpHeaders | undefined {
        const token = this.appStore.tkn();
        if (token) {
            headers = headers ?? new HttpHeaders();
            headers = headers.append('Authorization', `Bearer ${token}`);
        }
        return headers ?? undefined;
    }

    private validateToken(
        headers: HttpHeaders | null,
    ): Observable<HttpHeaders | undefined> {
        //check and verify if token is expired
        const tkn = this.appStore.tkn();
        if (!tkn || this.msalAuthService.isTokenExpired(tkn)) {
            return from(this.msalAuthService.getAccessToken()).pipe(
                concatMap(() => of(this.getHeaders(headers))),
                catchError(() => of(this.getHeaders(headers))),
            );
        }
        return of(this.getHeaders(headers));
    }

    private getApiUrl(baseUrl: string | null, endpointUrl: string): string {
        return `${baseUrl ? baseUrl : this.apiUrl}${endpointUrl}`;
    }

    private getCustomErrors(httpError: HttpErrorResponse): ICustomErrorResponse {
        const ce: ICustomErrorResponse = {
            status: httpError.status,
            messages: [],
            isValidationError: false,
            messagesInfo: [],
        };
        let { status, messages, isValidationError, messagesInfo } = ce;
        status = httpError.status;
        messages = [];
        messagesInfo = [];
        const noErrorProp = !httpError.error;
        isValidationError =
            !noErrorProp && httpError.status == (HttpStatusCode.BadRequest as number);

        if (isValidationError) {
            isValidationError = true;
            const ve: IValidationErrorResponse =
                httpError.error as IValidationErrorResponse;
            for (const key in ve.errors) {
                // eslint-disable-next-line no-prototype-builtins
                if (ve.errors.hasOwnProperty(key)) {
                    const keyErrors = ve.errors[key];
                    // eslint-disable-next-line @typescript-eslint/no-for-in-array
                    for (const kIndex in keyErrors) {
                        const message = keyErrors[kIndex];
                        messages.push(message);
                        messagesInfo.push({
                            id: key,
                            message: message,
                        });
                    }
                }
            }
        }

        if (
            !isValidationError ||
            httpError.status == (HttpStatusCode.InternalServerError as number)
        ) {
            const { MESSAGE__UnknownError } = this.translate();
            messages.push(formatString(MESSAGE__UnknownError, httpError.status));
        }

        return {
            status,
            messages,
            isValidationError,
            messagesInfo,
        };
    }
}
